import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowRight, Image as ImageIcon, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { useAuth } from "@/context/AuthContext";
import { dictionary, Language } from "@/utils/dictionary";
import { axiosInstanceConversationalEngine } from "@/services/instance";
import { getProductsPage } from "@/services/products/productAxios";
import type { Product } from "@/components/products/ProductInterface";
import type { PreviousProduct } from "@/store/onboardingStore";
import SearchPreviousProducts from "@/components/onboarding/components/SearchPreviousProducts";
import { ProductLinkInput } from "@/components/onboardingNew/ui/ProductLinkInput";
import { ProductManualInputs } from "@/components/onboardingNew/ui/ProductManualInputs";
import { ConfirmScrapedProductView } from "@/components/onboardingNew/ui/ConfirmScrapedProductView";
import { useGenericImageUpload } from "@/hooks/useGenericImageUpload";
import { FOLDER_IMAGES, MAX_FILE_SIZE, MAX_IMAGES } from "@/components/onboarding/const/products";
import type { VideoProductInput, VideoProductSource } from "@/services/ads/videos/videoDraft.types";

type ProductMethod = "link" | "manual" | "previous";

type ProductDraft = VideoProductInput & {
  price?: number | string | null;
};

type CatalogProduct = PreviousProduct & {
  website_id?: string | null;
  linked_funnels?: Array<{ website_id?: string | null }>;
  base_image_url?: string | null;
  best_image_url?: string | null;
};

interface VideoProductSelectionStepProps {
  onSelectProduct: (product: VideoProductInput) => void;
  onLayoutModeChange?: (mode: "compact" | "expanded") => void;
}

function normalizeImages(images?: Array<string | null | undefined>) {
  return (images ?? [])
    .map((image) => image?.trim())
    .filter((image): image is string => Boolean(image));
}

function productImage(product: CatalogProduct | ProductDraft) {
  const anyProduct = product as CatalogProduct;
  return (
    anyProduct.best_image_url ||
    anyProduct.base_image_url ||
    (product as ProductDraft).image_url ||
    normalizeImages(product.images)[0] ||
    null
  );
}

function linkedWebsiteId(product: CatalogProduct) {
  return product.website_id ?? product.linked_funnels?.find((funnel) => funnel.website_id)?.website_id ?? null;
}

function catalogProductToDraft(product: CatalogProduct): ProductDraft {
  const images = normalizeImages([
    product.best_image_url,
    product.base_image_url,
    ...(product.images ?? []),
  ]);

  return {
    source: "previous",
    product_id: product.id,
    website_id: linkedWebsiteId(product),
    name: product.name ?? "",
    description: product.description ?? "",
    image_url: images[0] ?? null,
    images,
    external_id: product.id,
    provider_id: "INTERNAL_FLUXI",
    price: product.price,
  };
}

function productToCatalogProduct(product: Product): CatalogProduct {
  return {
    ...product,
    country_code: product.country_code ?? undefined,
    currency_code: product.currency_code ?? undefined,
    price: product.price,
    images: normalizeImages([
      (product as Product & { best_image_url?: string | null }).best_image_url,
      (product as Product & { base_image_url?: string | null }).base_image_url,
      ...(product.images ?? []),
    ]),
    base_image_url: (product as Product & { base_image_url?: string | null }).base_image_url,
    best_image_url: (product as Product & { best_image_url?: string | null }).best_image_url,
  };
}

function scrapedProductToDraft(raw: any, productLink: string): ProductDraft {
  const images = normalizeImages(raw?.images);
  return {
    source: "link",
    product_id: null,
    website_id: null,
    name: raw?.name && raw.name !== "unknown" ? raw.name : "",
    description: raw?.description && raw.description !== "unknown" ? raw.description : "",
    image_url: images[0] ?? null,
    images,
    provider_id: raw?.provider_id ?? null,
    external_id: raw?.external_id ?? null,
    product_link: productLink,
  };
}

function cleanProductForVideo(product: ProductDraft): VideoProductInput {
  const images = normalizeImages([
    product.image_url,
    ...(product.images ?? []),
  ]);

  return {
    source: product.source,
    product_id: product.product_id ?? null,
    website_id: product.website_id ?? null,
    name: product.name.trim(),
    description: product.description.trim(),
    image_url: images[0] ?? null,
    images,
    provider_id: product.provider_id ?? null,
    external_id: product.external_id ?? null,
    product_link: product.product_link ?? null,
  };
}

export function VideoProductSelectionStep({ onSelectProduct, onLayoutModeChange }: VideoProductSelectionStepProps) {
  const { language, country } = useAuth();
  const t = (key: string, fallback: string) => dictionary[key]?.[language as Language] ?? fallback;
  const [method, setMethod] = useState<ProductMethod>("link");
  const [linkValue, setLinkValue] = useState("");
  const [manualData, setManualData] = useState({
    name: "",
    price: "0.00",
    description: "",
    images: [] as string[],
  });
  const [previousProducts, setPreviousProducts] = useState<CatalogProduct[]>([]);
  const [selectedPreviousProduct, setSelectedPreviousProduct] = useState<CatalogProduct | null>(null);
  const [previousLoading, setPreviousLoading] = useState(false);
  const [previousError, setPreviousError] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [confirmDraft, setConfirmDraft] = useState<ProductDraft | null>(null);

  useEffect(() => {
    onLayoutModeChange?.(confirmDraft || method === "manual" ? "expanded" : "compact");
  }, [confirmDraft, method, onLayoutModeChange]);

  const {
    isLoading: isUploadingImages,
    handleImageUpload,
    removeImage,
  } = useGenericImageUpload({
    folder: FOLDER_IMAGES,
    currentImages: manualData.images,
    onImagesUpdate: (images) => setManualData((prev) => ({ ...prev, images })),
    allowMultiple: true,
    maxImages: MAX_IMAGES,
    maxFileSize: MAX_FILE_SIZE,
  });

  const loadProducts = useCallback(async (search = "") => {
    const response = await getProductsPage({
      page: 0,
      size: 15,
      search: search.trim() || undefined,
    });
    return response.items.map(productToCatalogProduct);
  }, []);

  const loadInitialProducts = useCallback(() => {
    let cancelled = false;
    setPreviousLoading(true);
    setPreviousError(false);
    loadProducts()
      .then((items) => {
        if (!cancelled) {
          setPreviousProducts(items);
          setPreviousError(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPreviousProducts([]);
          setPreviousError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setPreviousLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadProducts]);

  useEffect(() => loadInitialProducts(), [loadInitialProducts]);

  const handleSearchProducts = useCallback(
    async (search: string): Promise<PreviousProduct[]> => {
      const items = await loadProducts(search);
      return items;
    },
    [loadProducts]
  );

  const handleManualDataChange = (field: string, value: string) => {
    setManualData((prev) => ({ ...prev, [field]: value }));
  };

  const beginConfirm = (product: ProductDraft) => {
    setConfirmDraft({
      ...product,
      image_url: productImage(product),
      images: normalizeImages([productImage(product), ...(product.images ?? [])]),
    });
  };

  const handleContinueLink = async () => {
    const productLink = linkValue.trim();
    if (!productLink) {
      toast.error("Pega un link de producto para continuar.");
      return;
    }

    setScraping(true);
    try {
      const response = await axiosInstanceConversationalEngine.post("/scrape-product", {
        product_url: productLink,
        country,
      });
      const productData = response.data?.data ?? {};
      const draft = scrapedProductToDraft(productData, productLink);
      const hasAnyData = draft.name || draft.description || (draft.images?.length ?? 0) > 0;
      if (!hasAnyData) {
        toast.error(t("product-not-found", "No encontramos datos suficientes de ese producto."));
        return;
      }
      beginConfirm(draft);
    } catch (error) {
      console.error("[VideoProductSelection] scrape failed", error);
      toast.error(t("failed-to-fetch-product-data", "No pudimos traer los datos del producto."));
    } finally {
      setScraping(false);
    }
  };

  const handleContinueManual = () => {
    beginConfirm({
      source: "manual",
      product_id: null,
      website_id: null,
      name: manualData.name,
      description: manualData.description,
      image_url: manualData.images[0] ?? null,
      images: manualData.images,
    });
  };

  const canContinueManual = useMemo(() => (
    manualData.name.trim().length > 0 &&
    manualData.description.trim().length > 0 &&
    manualData.images.length > 0 &&
    !isUploadingImages
  ), [isUploadingImages, manualData.description, manualData.images.length, manualData.name]);

  const canContinue = method === "link"
    ? Boolean(linkValue.trim()) && !scraping
    : method === "manual"
      ? canContinueManual
      : Boolean(selectedPreviousProduct) && !previousLoading && !previousError;

  const createVideoLabel = language === Language.ENGLISH
    ? "create your video"
    : language === Language.PORTUGUESE
      ? "criar seu video"
      : "crear tu video";

  const methodButtonClass = (active: boolean) =>
    `px-6 py-3 rounded-full font-medium transition-all border-2 flex items-center gap-2 disabled:opacity-60 disabled:pointer-events-none ${
      active
        ? "bg-[#2B3674] text-white border-[#2B3674]"
        : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
    }`;

  const handleContinue = () => {
    if (method === "link") {
      handleContinueLink();
      return;
    }

    if (method === "manual") {
      handleContinueManual();
      return;
    }

    if (method === "previous" && selectedPreviousProduct) {
      beginConfirm(catalogProductToDraft(selectedPreviousProduct));
    }
  };

  if (confirmDraft) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-white p-6 md:p-8">
        <ConfirmScrapedProductView
          title="Confirma los datos del producto para tu video"
          name={confirmDraft.name}
          description={confirmDraft.description}
          images={confirmDraft.images ?? []}
          onNameChange={(name) => setConfirmDraft((prev) => prev ? { ...prev, name } : prev)}
          onDescriptionChange={(description) => setConfirmDraft((prev) => prev ? { ...prev, description } : prev)}
          onImagesUpdate={(images) => setConfirmDraft((prev) => prev ? { ...prev, images, image_url: images[0] ?? null } : prev)}
          onBack={() => setConfirmDraft(null)}
          onConfirm={() => onSelectProduct(cleanProductForVideo(confirmDraft))}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-white p-8 md:p-12">
      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        <div className={`${method === "manual" ? "max-w-3xl flex flex-col flex-1 min-h-0" : "max-w-lg"} mx-auto w-full`}>
          <div className={`text-center mb-6 min-h-[5.5rem] flex items-center justify-center ${method === "manual" ? "md:px-40" : ""}`}>
            <h1 className="text-4xl font-bold mb-2">
              <span className="text-gray-900">{t("load-a-product-to-create-your-page", "Carga un producto para ")}</span>
              <span className="text-fluxi">{createVideoLabel}</span>
            </h1>
          </div>

          <div id="chooseOption-target" className="flex gap-3 justify-center mb-6">
            <button
              type="button"
              disabled={scraping}
              onClick={() => setMethod("link")}
              className={methodButtonClass(method === "link")}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              {t("link", "Link")}
            </button>
            <button
              type="button"
              disabled={scraping}
              onClick={() => setMethod("manual")}
              className={methodButtonClass(method === "manual")}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {t("upload-photo", "Subir foto")}
            </button>
            <button
              type="button"
              disabled={scraping}
              onClick={() => setMethod("previous")}
              className={methodButtonClass(method === "previous")}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {t("your-products", "Tus productos")}
            </button>
          </div>

          {method === "link" ? (
            <div className="mx-auto max-w-xl space-y-4">
              <ProductLinkInput
                value={linkValue}
                onChange={setLinkValue}
                placeholder={t("paste-product-url-placeholder", "Pega el link del producto")}
              />
            </div>
          ) : null}

          {method === "manual" ? (
            <div className="flex-1 min-h-0 mt-1">
              <ProductManualInputs
                manualData={manualData}
                handleImageUpload={handleImageUpload}
                handleRemoveImage={removeImage}
                handleManualDataChange={handleManualDataChange}
                isUploadingImages={isUploadingImages}
              />
            </div>
          ) : null}

          {method === "previous" ? (
            <div className="mx-auto max-w-xl space-y-4">
              {previousLoading ? (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-8 text-center">
                  <Loader2 className="mx-auto h-6 w-6 animate-spin text-fluxi" />
                  <p className="mt-3 text-sm text-slate-500">{t("loading-products", "Cargando productos...")}</p>
                </div>
              ) : previousError ? (
                <div className="text-center py-8">
                  <ImageIcon className="w-16 h-16 mx-auto text-gray-300 mb-3" />
                  <p className="text-sm font-medium text-gray-700">No pudimos cargar tus productos.</p>
                  <p className="mt-1 text-sm text-gray-500">Revisa el servicio local o intenta de nuevo.</p>
                  <button
                    type="button"
                    onClick={loadInitialProducts}
                    className="mt-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                  >
                    Reintentar
                  </button>
                </div>
              ) : previousProducts.length === 0 ? (
                <div className="text-center py-8">
                  <ImageIcon className="w-16 h-16 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-600">{t("no-previous-products-available", "No hay productos guardados.")}</p>
                </div>
              ) : (
                <SearchPreviousProducts
                  previousProducts={previousProducts}
                  isPreviousProductsLoading={previousLoading}
                  selectedProductId={selectedPreviousProduct?.id ?? null}
                  handlePreviousProductSelect={(product) => setSelectedPreviousProduct(product as CatalogProduct)}
                  searchProducts={handleSearchProducts}
                />
              )}
            </div>
          ) : null}
        </div>
      </div>
      <div className="mt-8 flex shrink-0 justify-end">
        <button
          type="button"
          disabled={!canContinue}
          onClick={handleContinue}
          className={`${!canContinue ? "bg-[#E2E8F0]" : "bg-fluxi hover:bg-fluxi-dark"} ml-auto flex items-center gap-2 px-7 py-2.5 text-sm text-white rounded-full font-medium transition-all disabled:cursor-not-allowed`}
        >
          {scraping ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
          {t("continue", "Continuar")}
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
